import React from "react";
import { EyeCatcherTypeEnum } from "../../../types/article-price";
import { highlightText } from "../../../util/highlightText";
import { IProductTileProps } from "../../../types/suggestion-items";
import { ITelemetryData } from "../../../types/telemetry";
import { sendTelemetryData } from "../../../util/events/telemetry";
import { setStoredSearchQuery } from "../../../util/queryStorage";
import { useL10nContext, useNavigationContext } from "../../../util/contexts";
import ArticlePrice from "./article-price/article-price";
import EyeCatcher from "./eye-catcher/eye-catcher";
import FlexibleImageGrid from "./flexible-image-grid/flexible-image-grid";

const ProductTile = (props: IProductTileProps) => {
    const { handleMouseSelect, handleMouseDeselect, handleTouched } = useNavigationContext();
    const { decodeVariantsL10n, imageMainView } = useL10nContext();
    const variantsL10n = decodeVariantsL10n();

    const imageAltText = `${imageMainView}, ${props.article.title}`;
    const trackingId = props.isPersonalized
        ? "ssw-zq-suggested-articles"
        : "ssw-suggested-articles";
    const formattedName = props.isPersonalized
        ? props.article.title
        : highlightText(props.article.title, props.searchInfo.searchTerm);
    const articleClassName = props.isPersonalized
        ? `suggestion-item ${props.article.type.toLowerCase()} recommendation`
        : `suggestion-item ${props.article.type.toLowerCase()}`;

    const shouldShowEyeCatcher =
        props.article.price?.discountPrice?.discountPercentage !== undefined;

    const variantString = () => {
        if (props.article.availableColorsCount > 0) {
            const colorLabel =
                props.article.availableColorsCount > 1 ? variantsL10n.colors : variantsL10n.color;
            return `${props.article.availableColorsCount} ${colorLabel}`;
        } else if (props.article.availableModelsCount > 0) {
            const modelLabel =
                props.article.availableModelsCount > 1
                    ? variantsL10n.variants
                    : variantsL10n.variant;
            return `${props.article.availableModelsCount} ${modelLabel}`;
        } else return "";
    };

    const handleClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
        event.preventDefault();
        handleTouched(props.article.navIndex);
        if (!props.isPersonalized) recordProductUsed();
        setStoredSearchQuery({ name: props.article.title, target: props.article.target });
        window.location.href = props.article.target;
    };

    const recordProductUsed = () => {
        const data: ITelemetryData = {
            correlationId: props.searchInfo.pageViewLogDataId,
            culture: props.searchInfo.culture,
            portal: props.searchInfo.portal,
            searchTerm: props.searchInfo.searchTerm,
            shortLink: props.article.shortLink,
            suggestion: props.article.title,
            contentType: props.article.type
        };
        sendTelemetryData(data, props.searchInfo.trackingEndpoint);
    };

    return (
        <div
            ref={(el) => props.setRef(props.article.navIndex, el)}
            onMouseEnter={() => handleMouseSelect(props.article.navIndex)}
            onMouseLeave={handleMouseDeselect}
            className={articleClassName}
        >
            <a
                className={`ssw-suggested-articles-${props.index}`}
                href={props.article.target}
                onClick={handleClick}
                data-testid={`${trackingId}-${props.index}`}
                data-semantic-id={`${trackingId}-${props.index}`}
            >
                <FlexibleImageGrid
                    article={props.article}
                    set={null}
                    imageMainView={imageAltText}
                />
                <div className="article-info">
                    <span className="article-name">{formattedName}</span>
                    {shouldShowEyeCatcher && props.article.price && (
                        <EyeCatcher
                            discountPercentage={
                                props.article.price.discountPrice!.discountPercentage
                            }
                            eyeCatherType={EyeCatcherTypeEnum.Sale}
                        />
                    )}
                    {props.showPrices && props.article.price && (
                        <ArticlePrice
                            prices={props.article.price}
                            priceSettings={props.priceMandantSettings}
                        />
                    )}
                    <div className="article-variants">{variantString()}</div>
                </div>
            </a>
        </div>
    );
};

export default ProductTile;
